import React, { useEffect, useState } from "react";
import { Link, NavLink, NavLinkProps, useHistory } from "react-router-dom";
import { onLogout } from "../../api-client/APIClient";
import { ReactComponent as Logo } from "../../assets/svg/logos/merge/logo.svg";
import { palette, spectrum } from "../../styles/theme";
import {
  Lock,
  LogOut,
  Globe,
  Key,
  FileSearch,
  ArrowUpRight,
  Settings,
  Settings2,
  Library,
  Link as LinkIcon,
  Map,
  Code,
  ChevronRight,
  BarChart2,
  AlertCircle,
  Gem,
  Sparkles,
  BookOpen,
  Github,
} from "lucide-react";
import {
  API_TESTER_PATH,
  BILLING_PATH,
  CONFIGURATION_PATH,
  ONBOARDING_PATH,
  ONBOARDING_GET_STARTED_ADD_LINK_TO_APP_PATH,
  ONBOARDING_GET_STARTED_INTEGRATE_SYNCED_DATA_PATH,
  ONBOARDING_GET_STARTED_SEND_API_REQUEST_PATH,
  ONBOARDING_GET_STARTED_SYNC_SANDBOX_DATA_PATH,
  ONBOARDING_GET_STARTED_TOGGLE_SCOPES_PATH,
  ONBOARDING_ADVANCED_SYNCING_DATA_PATH,
  ONBOARDING_ADVANCED_MERGE_WRITES_PATH,
  ONBOARDING_ADVANCED_WEBHOOKS_PATH,
  ONBOARDING_ADVANCED_METADATA_PATH,
  ONBOARDING_ADVANCED_SUPPLEMENTAL_DATA_PATH,
  ONBOARDING_AI_SKILLS_PATH,
  CONFIGURATION_INTEGRATIONS_PATH,
  CONFIGURATION_COMMON_MODELS_PATH,
  DASHBOARD_PATH,
  DOCS_BASE_PATH,
  ISSUES_PATH,
  LINKED_ACCOUNTS_PATH,
  LOGIN_PATH,
  LOGS_PATH,
  MERGE_HELP_CENTER_PATH,
  PROFILE_PATH,
  API_KEYS_PATH,
  API_TESTER_YOU_TO_MERGE_LINKED_ACCOUNT_PATH,
  API_TESTER_MERGE_TO_THIRD_PARTY_PATH,
  API_TESTER_YOU_TO_MERGE_MOCK_SANDBOX_PATH,
  CONFIGURATION_WEBHOOKS_EMITTERS_PATH,
  GET_IN_TOUCH_PATH,
  navigateToHomePage,
} from "../../router/RouterUtils";
import useAppContext from "../context/useAppContext";
import { UserAvatar, UserAvatarSize } from "../shared/MergeAvatars";
import { showErrorToast } from "../shared/Toasts";
import LiveWaitingIndicator from "../pages/get-started/components/shared/LiveWaitingIndicator";
import { DashboardOnboardingStatus } from "../pages/get-started/types";
import { INVOICE_FE_DISABLED_STATUSES } from "../../models/Entities";
import { isRouteDisabled } from "../shared/utils/SharedComponentUtils";
import UpsellButton from "../shared/UpsellButton";
import { isBillingPlanFreeLaunch } from "../pages/settings/billing/BillingUtils";
import { userEventTracker } from "../shared/banners/DashboardBanners";
import GetStartedTaskCompleteCounter from "./GetStartedTaskCompleteCounter";
import { Card, Text } from "@merge-api/merge-javascript-shared";
import clsx from "clsx";
import useProductSwitcher from "./useProductSwitcher";
import ProductSwitcherNav from "./ProductSwitcherNav";
import useCommandKPressed from "../../hooks/useCommandKPressed";
import InkeepModal from "../inkeep/InkeepModal";
import { useFrigadeAssistantEnabled } from "../shared/hooks/useFrigadeAssistantEnabled";
import { useFlow } from "@frigade/react";
import { upgradeRequestFlowId } from "../../router/FrigadeProvider";

type Props = {
  dashboardOnboardingStatus: DashboardOnboardingStatus | null;
  mobileOpen: boolean;
  onMobileClose: () => void;
};

interface DisableableNavLinkProps extends NavLinkProps {
  disabled?: boolean;
  children: React.ReactNode;
}

export const DisableableNavLink = ({ disabled, children, ...props }: DisableableNavLinkProps) => {
  return (
    <NavLink
      className={`flex flew-row items-center gap-x-2 nav-link px-4 py-2 ${
        disabled ? "disabled" : ""
      }`}
      {...props}
    >
      {children}
    </NavLink>
  );
};

// We have arbitrarily chosen 15 days to hide the left nav bar upsell
// to give customers a chance to upgrade and explore the new features
// before immediately upselling them.
const DAYS_TO_HIDE_LEFT_NAV_UPSELL = 15;

// InKeep is off everywhere. It's a third-party AI chat widget, so serving it as the
// fallback would have handed orgs on the AI opt-out list another AI surface. They get
// Pylon instead, which is human support. Left wired up so this is one line to reverse.
const INKEEP_ENABLED = false;

function LeftNavigationBar({ dashboardOnboardingStatus, mobileOpen, onMobileClose }: Props) {
  const { user } = useAppContext();
  const history = useHistory();
  const switcher = useProductSwitcher();
  const showProductSwitcher = switcher.enabled && switcher.currentProduct != null;
  const isProductPaneOpen = showProductSwitcher && switcher.isPaneOpen;
  const dashboardDisabledForBilling =
    user.organization.invoice_overdue_status &&
    INVOICE_FE_DISABLED_STATUSES.includes(user.organization.invoice_overdue_status);
  const { flow: upgradeRequestFlow } = useFlow(upgradeRequestFlowId);

  const {
    enabled: frigadeAssistantEnabled,
    hasInitialized: frigadeAssistantInitialized,
    excluded: frigadeAssistantExcluded,
  } = useFrigadeAssistantEnabled(user.organization.id);
  // Waits for initialization rather than defaulting to InKeep while the flags load, so
  // an opted-out org can't be shown an AI surface for the first few hundred ms.
  const showInkeep =
    INKEEP_ENABLED &&
    frigadeAssistantInitialized &&
    !frigadeAssistantEnabled &&
    !frigadeAssistantExcluded;
  const [showInkeepModal, setShowInkeepModal] = useState(false);
  const commandKPressed = useCommandKPressed();
  useEffect(() => {
    if (!commandKPressed) return;
    // No-op until the flags resolve. Routing to Pylon early would open the support chat
    // for an org that turns out to have the Assistant, and FrigadeAssistant then hides
    // #pylon-chat on init — pulling the window out from under someone mid-sentence.
    if (!frigadeAssistantInitialized) return;
    if (frigadeAssistantEnabled) {
      // Empty question opens the widget with a blank input focused; the SDK treats
      // ask({ question: "" }) as "just open the panel". No-op before the CDN loads
      // (stub.ask is undefined), which is acceptable — the window is <1s.
      window.FrigadeAssistant?.ask?.({ question: "" });
    } else if (showInkeep) {
      setShowInkeepModal(true);
    } else {
      // Keeps a keyboard route to help now that the Ask AI entry point is gone.
      window.Pylon?.("show");
    }
  }, [commandKPressed, frigadeAssistantInitialized, frigadeAssistantEnabled, showInkeep]);

  const shouldHomePageDefaultToDashboard =
    (dashboardOnboardingStatus?.has_made_request &&
      dashboardOnboardingStatus?.has_successfully_linked_account &&
      dashboardOnboardingStatus?.has_opened_production_link &&
      dashboardOnboardingStatus?.has_pulled_common_model_data &&
      dashboardOnboardingStatus?.has_toggled_scopes) ??
    false;
  const isLoadingOnboardingStatus = !dashboardOnboardingStatus;
  const startShowingLeftNavUpsell = new Date();
  startShowingLeftNavUpsell.setDate(
    startShowingLeftNavUpsell.getDate() - DAYS_TO_HIDE_LEFT_NAV_UPSELL,
  );

  const startOfCurrentBillingPlan = new Date(
    user.organization.organization_billing_plan?.start_date || "",
  );

  const logout = () =>
    onLogout({
      onError: () => showErrorToast(`Failed to logout. Please try again.`),
    });

  const dropdownOptions = (
    <>
      <Link
        to={dashboardDisabledForBilling ? BILLING_PATH : PROFILE_PATH}
        className="dropdown-item flex flex-row items-center text-black hover:text-gray-50"
      >
        <Settings size={14} className="mr-2.5" />
        Settings
      </Link>
      <a
        href={DOCS_BASE_PATH}
        className="dropdown-item flex flex-row items-center text-black hover:text-gray-50"
        target="_blank"
        rel="noreferrer"
      >
        <BookOpen size={14} className="mr-2.5" />
        Docs
        <ArrowUpRight size={14} className="text-right ml-auto" />
      </a>
      <a
        href="https://github.com/merge-api/merge-unified-skills"
        className="dropdown-item flex flex-row items-center text-black hover:text-gray-50"
        target="_blank"
        rel="noreferrer"
      >
        <Github size={14} className="mr-2.5" />
        AI skills repo
        <ArrowUpRight size={14} className="text-right ml-auto" />
      </a>
      <a
        href={MERGE_HELP_CENTER_PATH}
        className="dropdown-item flex flex-row items-center text-black hover:text-gray-50"
        target="_blank"
        rel="noreferrer"
      >
        <Library size={14} className="mr-2.5" />
        Help Center
        <ArrowUpRight size={14} className="text-right ml-auto" />
      </a>
      <hr className="border-gray-10 my-3 overflow-hidden h-0" />
      <NavLink
        className="dropdown-item flex flex-row items-center text-black hover:text-gray-50"
        to={LOGIN_PATH}
        onClick={logout}
      >
        <LogOut size={14} className="mr-2.5" />
        Logout
      </NavLink>
    </>
  );

  return (
    <>
      {showInkeep && (
        <InkeepModal showInkeepModal={showInkeepModal} setShowInkeepModal={setShowInkeepModal} />
      )}
      {/* Backdrop overlay for mobile */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 md:hidden" onClick={onMobileClose} />
      )}
      <nav
        className={`navbar navbar-vertical flex flex-col items-start shrink-0 fixed-left navbar-expand-md navbar-light bg-gray-0 transition-transform duration-300 ease-in-out ${
          mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
        id="sidebar"
      >
        {/* Logo - hidden on mobile (shown in AppRouter mobile header instead) */}
        <div className="hidden md:flex justify-start pt-6 pb-5 px-6 w-full">
          <Logo className="hover:cursor-pointer" onClick={() => navigateToHomePage(history)} />
        </div>

        {/* Nav */}
        <div className="navbar-collapse h-full px-2 w-full bg-gray-0" id="sidebarCollapse">
          {showProductSwitcher && (
            <ProductSwitcherNav
              switcher={switcher}
              onContactSupport={() => window.Pylon?.("show")}
            />
          )}
          {/* While the pane is open the nav items are hidden, not unmounted, so they come back as they were */}
          {/* Get started */}
          {!isLoadingOnboardingStatus && !shouldHomePageDefaultToDashboard && (
            <ul className={clsx("navbar-nav my-4", isProductPaneOpen && "hidden")}>
              <li className="nav-item">
                <DisableableNavLink
                  to={ONBOARDING_PATH}
                  isActive={(_, location) => {
                    const path = location.pathname;

                    return (
                      [
                        ONBOARDING_GET_STARTED_TOGGLE_SCOPES_PATH,
                        ONBOARDING_GET_STARTED_SYNC_SANDBOX_DATA_PATH,
                        ONBOARDING_GET_STARTED_SEND_API_REQUEST_PATH,
                        ONBOARDING_GET_STARTED_INTEGRATE_SYNCED_DATA_PATH,
                        ONBOARDING_GET_STARTED_ADD_LINK_TO_APP_PATH,
                        ONBOARDING_ADVANCED_SYNCING_DATA_PATH,
                        ONBOARDING_ADVANCED_MERGE_WRITES_PATH,
                        ONBOARDING_ADVANCED_WEBHOOKS_PATH,
                        ONBOARDING_ADVANCED_METADATA_PATH,
                        ONBOARDING_ADVANCED_SUPPLEMENTAL_DATA_PATH,
                        ONBOARDING_AI_SKILLS_PATH,
                      ].includes(path) ||
                      (!isLoadingOnboardingStatus &&
                        !shouldHomePageDefaultToDashboard &&
                        path == "/")
                    );
                  }}
                >
                  <Map size={16} /> Get started
                  <i
                    className="text-right mx-auto"
                    style={{ display: "flex", alignItems: "center" }}
                  />
                  {!shouldHomePageDefaultToDashboard && (
                    <GetStartedTaskCompleteCounter
                      dashboardOnboardingStatus={dashboardOnboardingStatus}
                    />
                  )}
                </DisableableNavLink>
              </li>
            </ul>
          )}

          {/* Nav Links */}
          <ul className={clsx("navbar-nav flex flex-1", isProductPaneOpen && "hidden")}>
            <Text variant="pre-title" className="text-gray-40 px-4 py-2">
              Overview
            </Text>
            <li className="nav-item">
              <DisableableNavLink
                exact
                to={DASHBOARD_PATH}
                disabled={isRouteDisabled(user, DASHBOARD_PATH)}
                isActive={(_, location) => {
                  const path = location.pathname;
                  return (
                    path == DASHBOARD_PATH ||
                    (!isLoadingOnboardingStatus &&
                      shouldHomePageDefaultToDashboard &&
                      !isRouteDisabled(user, DASHBOARD_PATH) &&
                      path == "/")
                  );
                }}
              >
                <BarChart2 size={16} /> Dashboard
              </DisableableNavLink>
            </li>
            <li className="nav-item">
              <DisableableNavLink
                to={LINKED_ACCOUNTS_PATH}
                disabled={isRouteDisabled(user, LINKED_ACCOUNTS_PATH)}
              >
                <LinkIcon size={16} /> Linked Accounts
              </DisableableNavLink>
            </li>
            <li className="nav-item">
              <DisableableNavLink to={LOGS_PATH} disabled={isRouteDisabled(user, LOGS_PATH)}>
                <FileSearch
                  color={isRouteDisabled(user, LOGS_PATH) ? spectrum.gray50 : palette.black}
                  size={16}
                />
                Logs
              </DisableableNavLink>
            </li>
            <li className="nav-item">
              <DisableableNavLink disabled={isRouteDisabled(user, ISSUES_PATH)} to={ISSUES_PATH}>
                <AlertCircle size={16} /> Issues
              </DisableableNavLink>
            </li>
            <Text variant="pre-title" className="text-gray-40 px-4 py-2">
              Configure
            </Text>
            <li className="nav-item">
              <DisableableNavLink
                disabled={isRouteDisabled(user, CONFIGURATION_INTEGRATIONS_PATH)}
                to={CONFIGURATION_INTEGRATIONS_PATH}
              >
                <Globe
                  color={
                    isRouteDisabled(user, CONFIGURATION_INTEGRATIONS_PATH)
                      ? spectrum.gray50
                      : palette.black
                  }
                  size={16}
                />
                Integrations
              </DisableableNavLink>
            </li>
            <li className="nav-item " id="tooltip-select-scopes-left-nav">
              <DisableableNavLink
                disabled={isRouteDisabled(user, CONFIGURATION_COMMON_MODELS_PATH)}
                to={CONFIGURATION_COMMON_MODELS_PATH}
              >
                <Lock
                  color={
                    isRouteDisabled(user, CONFIGURATION_COMMON_MODELS_PATH)
                      ? spectrum.gray50
                      : palette.black
                  }
                  size={16}
                />
                Scopes
              </DisableableNavLink>
            </li>
            <li className="nav-item" id="tooltip-select-advanced-left-nav">
              <DisableableNavLink
                disabled={isRouteDisabled(user, CONFIGURATION_PATH)}
                to={CONFIGURATION_WEBHOOKS_EMITTERS_PATH}
                isActive={(_, location) => {
                  const path = location.pathname;
                  return path.includes(CONFIGURATION_PATH);
                }}
              >
                <Settings2
                  color={
                    isRouteDisabled(user, CONFIGURATION_PATH) ? spectrum.gray50 : palette.black
                  }
                  size={16}
                />
                Advanced
              </DisableableNavLink>
            </li>
            <Text variant="pre-title" className="text-gray-40 px-4 py-2">
              Resources
            </Text>
            {/* API tester */}
            <li className="nav-item">
              <DisableableNavLink
                disabled={isRouteDisabled(user, API_TESTER_PATH)}
                to={API_TESTER_YOU_TO_MERGE_LINKED_ACCOUNT_PATH}
                isActive={(_, location) => {
                  const path = location.pathname;

                  return (
                    path === API_TESTER_YOU_TO_MERGE_LINKED_ACCOUNT_PATH ||
                    path === API_TESTER_YOU_TO_MERGE_MOCK_SANDBOX_PATH ||
                    path === API_TESTER_MERGE_TO_THIRD_PARTY_PATH
                  );
                }}
              >
                <Code
                  color={isRouteDisabled(user, API_TESTER_PATH) ? spectrum.gray50 : palette.black}
                  size={16}
                />{" "}
                API tester
              </DisableableNavLink>
            </li>
            {/* API keys */}
            <li className="nav-item">
              <DisableableNavLink
                disabled={isRouteDisabled(user, API_KEYS_PATH)}
                to={API_KEYS_PATH}
              >
                <Key
                  color={isRouteDisabled(user, API_KEYS_PATH) ? spectrum.gray50 : palette.black}
                  size={16}
                />{" "}
                API keys
              </DisableableNavLink>
            </li>
            {showInkeep && (
              <li
                className="nav-item"
                onClick={() => {
                  setShowInkeepModal(true);
                }}
              >
                <div className="nav-link px-4 py-2 flex flew-row items-center gap-x-2 cursor-pointer">
                  <Sparkles size={16} />
                  <span>Ask AI</span>
                  <span className="text-right ml-auto">
                    <div className="ml-2.5 pl-1.5 pr-1.5 text-gray-40 text-xs rounded-3xl border-gray-10 border leading-4">
                      ⌘K
                    </div>
                  </span>
                </div>
              </li>
            )}
            {/* Get started */}
            {!isLoadingOnboardingStatus && shouldHomePageDefaultToDashboard && (
              <li className="nav-item">
                <DisableableNavLink
                  disabled={isRouteDisabled(user, ONBOARDING_PATH)}
                  to={ONBOARDING_PATH}
                  isActive={(_, location) => {
                    const path = location.pathname;

                    return (
                      path == ONBOARDING_GET_STARTED_TOGGLE_SCOPES_PATH ||
                      path == ONBOARDING_GET_STARTED_SYNC_SANDBOX_DATA_PATH ||
                      path == ONBOARDING_GET_STARTED_ADD_LINK_TO_APP_PATH ||
                      path == ONBOARDING_GET_STARTED_INTEGRATE_SYNCED_DATA_PATH ||
                      path == ONBOARDING_GET_STARTED_SEND_API_REQUEST_PATH
                    );
                  }}
                >
                  <Map size={16} /> Get started
                  <i
                    className="text-right mx-auto"
                    style={{ display: "flex", alignItems: "center" }}
                  />
                  {!isRouteDisabled(user, ONBOARDING_PATH) && (
                    <>{!shouldHomePageDefaultToDashboard && <LiveWaitingIndicator color="red" />}</>
                  )}
                </DisableableNavLink>
              </li>
            )}
            {isBillingPlanFreeLaunch(user.organization.organization_billing_plan?.billing_plan) &&
              user.organization.organization_billing_plan?.end_date === null &&
              startOfCurrentBillingPlan < startShowingLeftNavUpsell && (
                <div className="flex flex-col h-full w-full justify-end mb-3 flex-1">
                  <li className="nav-item flex">
                    <UpsellButton
                      onClick={() => {
                        upgradeRequestFlow?.skip();
                        window.open(GET_IN_TOUCH_PATH, "_blank");
                        userEventTracker({
                          event_name_input: "upsell_button_left_nav",
                          impressions_input: 0,
                          dismisses_input: 0,
                          clicks_input: 1,
                        });
                      }}
                      buttonContent={
                        <div className="flex flex-row items-center gap-x-2">
                          <Gem size={10} />
                          Request upgrade
                        </div>
                      }
                      buttonColor="gradientGold"
                      hasAdvancedHoverState={false}
                      id="upsell_button_left_nav"
                    />
                  </li>
                </div>
              )}
          </ul>
        </div>
        {/* User Settings */}
        <div className="navbar-user bg-gray-0 flex px-6 w-full" id="sidebarUser">
          <div className="dropup py-4 max-w-full">
            <div
              className="flex flex-row w-full justify-between items-center gap-x-3 truncate hover:cursor-pointer"
              role="button"
              data-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
            >
              {/* Sidebar avatar + name + org are user PII visible on every page and are thus masked at the section */}
              <div className="ph-no-capture flex">
                <UserAvatar user={user} size={UserAvatarSize.sm} />
              </div>
              <div className="ph-no-capture flex flex-col flex-grow truncate justify-start">
                <Text variant="h6" className="truncate text-left">
                  {user.name ? user.name : user.email}
                </Text>
                <Text variant="sm" className="truncate text-left text-gray-40">
                  {user.organization?.name ? user.organization?.name : null}
                </Text>
              </div>
              <div className="flex">
                <ChevronRight className="text-gray-40" size={16} />
              </div>
            </div>

            <Card className="dropdown-menu absolute max-h-96" aria-labelledby="sidebarIconCopy">
              {dropdownOptions}
            </Card>
          </div>
        </div>
      </nav>
    </>
  );
}

export default LeftNavigationBar;
